import { supabase, uid, isRecord } from './shared'
import { sortMessagesChronologically } from './messages'
import { attachDirectorNotesToMessages } from './director-notes'
import {
  mapConversationsFromSupabase,
  mapConversationFromSupabase,
  mapConversationResponseFromSupabase,
  mapConversationMessagesResponseFromSupabase,
} from '../../mappers/conversations'
import { getCharacter } from './characters'
import { getPersona } from './personas'
import { getBots } from './bots'
import type {
  ConversationCreateData,
  ConversationMessagesOptions,
  ConversationUpdateData,
} from '../../contract'
import type { EnsureConversationIntroSlotResponse, SingleConversationResponse } from '@/types/api'
import type { Conversation } from '@/types/domain'

// Must match the conversation session store's pageSize: the store derives
// hasMore from `bundled messages >= pageSize`, which is only exact when the
// bundle fetches exactly one page. 50 is also the baseline initial page size.
export const CONVERSATION_CONTEXT_MESSAGE_LIMIT = 50;

type MessageLikeWithIndex = {
  id: number | string
  conversation_id: number | string
  t?: string
  created_at?: string
  updated_at?: string
  director_note?: unknown
  [key: string]: unknown
}

type RawConversationMessageLike = MessageLikeWithIndex & {
  conversation_id: number | string
  director_note?: unknown
  t?: string
  created_at?: string
  updated_at?: string
}

export async function getConversations() {
  const userId = await uid();
  const { data, error } = await supabase
    .from('conversations')
    .select('*')
    .eq('user_id', userId)
    .order('last_message_at', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false });

  if (error) throw error;
  return { conversations: mapConversationsFromSupabase(data ?? []) };
}

export async function getConversation(id: number | string): Promise<SingleConversationResponse> {
  const userId = await uid();
  const { data, error } = await supabase
    .from('conversations')
    .select('*')
    .eq('user_id', userId)
    .eq('id', id)
    .single();

  if (error) throw error;
  return mapConversationResponseFromSupabase({ conversation: data });
}

export async function createConversation(conversationData: ConversationCreateData): Promise<Conversation> {
  const userId = await uid();
  const payload = {
    ...conversationData,
    user_id: userId,
    title: conversationData.title || 'Conversation',
  };

  const { data, error } = await supabase
    .from('conversations')
    .insert(payload)
    .select('*')
    .single();

  if (error) throw error;
  return mapConversationFromSupabase(data);
}

export async function forkConversationAtMessage(messageId: number | string): Promise<Conversation> {
  const { data, error } = await supabase.rpc('rpc_fork_conversation_at_message_v1', {
    p_message_id: Number(messageId),
  });

  if (error) throw error;
  if (!isRecord(data)) throw new Error('Invalid fork conversation response');

  return mapConversationFromSupabase(data);
}

export async function updateConversation(id: number | string, conversationData: ConversationUpdateData): Promise<SingleConversationResponse> {
  const userId = await uid();
  const { data, error } = await supabase
    .from('conversations')
    .update(conversationData)
    .eq('user_id', userId)
    .eq('id', id)
    .select('*')
    .single();

  if (error) throw error;
  return mapConversationResponseFromSupabase({ conversation: data });
}

export async function deleteConversation(id: number | string) {
  const userId = await uid();

  const { data: msgs, error: mErr } = await supabase
    .from('messages')
    .select('id')
    .eq('user_id', userId)
    .eq('conversation_id', id);

  if (mErr) throw mErr;

  const messageIds = (msgs ?? [])
    .map((m: unknown) => (isRecord(m) ? m.id : undefined))
    .filter((value): value is number | string => value !== undefined && value !== null);

  if (messageIds.length > 0) {
    const { error: dErr } = await supabase
      .from('messages')
      .delete()
      .in('id', messageIds)
      .eq('user_id', userId);
    if (dErr) throw dErr;
  }

  const { error } = await supabase
    .from('conversations')
    .delete()
    .eq('id', id)
    .eq('user_id', userId);

  if (error) throw error;
  return { message: 'Conversation deleted successfully' };
}

export async function getConversationMessages(conversationId: number | string, options: ConversationMessagesOptions = {}) {
  const userId = await uid();
  const { limit, before_id } = options || {};

  let query = supabase
    .from('messages')
    .select('*, active:active_variant_id(content, stream_id, is_streaming, is_complete)')
    .eq('user_id', userId)
    .eq('conversation_id', conversationId)
    .is('parent_message_id', null)
    // Insertion order (bigint serial id) is the canonical conversation order and
    // the pagination key. `before_id` is a keyset cursor over the same column.
    .order('id', { ascending: false });

  if (before_id !== undefined && before_id !== null) {
    query = query.lt('id', before_id);
  }

  if (limit !== undefined && limit !== null) {
    const limitValue = Number(limit);
    if (Number.isFinite(limitValue)) {
      query = query.limit(limitValue);
    }
  }

  const { data, error } = await query;

  if (error) throw error;
  const sorted = sortMessagesChronologically(await attachDirectorNotesToMessages((data ?? []) as MessageLikeWithIndex[], userId));
  return mapConversationMessagesResponseFromSupabase({ messages: sorted as RawConversationMessageLike[] });
}

async function getConversationMessagesForContext(conversationId: number | string) {
  const userId = await uid();
  const { data, error } = await supabase
    .from('messages')
    .select('*, active:active_variant_id(content, stream_id, is_streaming, is_complete)')
    .eq('user_id', userId)
    .eq('conversation_id', conversationId)
    .is('parent_message_id', null)
    // Most-recent N by insertion order, matching the canonical timeline order.
    .order('id', { ascending: false })
    .limit(CONVERSATION_CONTEXT_MESSAGE_LIMIT);

  if (error) throw error;
  const sorted = sortMessagesChronologically(await attachDirectorNotesToMessages((data ?? []) as MessageLikeWithIndex[], userId));
  return mapConversationMessagesResponseFromSupabase({ messages: sorted as RawConversationMessageLike[] });
}

export async function getConversationContext(conversationId: number | string) {
  const conversationResponse = await getConversation(conversationId);
  const conversation = conversationResponse.conversation;

  if (!conversation) {
    throw new Error('Conversation not found');
  }

  const [messagesResult, characterResult, personaResult, botsResult] = await Promise.allSettled([
    getConversationMessagesForContext(conversationId),
    conversation.character_id ? getCharacter(conversation.character_id) : null,
    conversation.persona_id ? getPersona(conversation.persona_id) : null,
    getBots(),
  ]);

  if (messagesResult.status === 'rejected') throw messagesResult.reason;
  const messages = messagesResult.value.messages || [];
  const character = characterResult.status === 'fulfilled'
    ? conversation.character_id && !characterResult.value
      ? { status: 'failed' as const }
      : { status: 'resolved' as const, value: characterResult.value ?? null }
    : { status: 'failed' as const };
  const persona = personaResult.status === 'fulfilled'
    ? conversation.persona_id && !personaResult.value
      ? { status: 'failed' as const }
      : { status: 'resolved' as const, value: personaResult.value ?? null }
    : { status: 'failed' as const };
  const bots = botsResult.status === 'fulfilled'
    ? {
        status: 'resolved' as const,
        value: isRecord(botsResult.value) && Array.isArray(botsResult.value.bots)
          ? botsResult.value.bots
          : [],
      }
    : { status: 'failed' as const };

  return {
    conversation,
    messages,
    character,
    persona,
    bots,
  };
}

export async function ensureConversationIntroSlot(
  conversationId: number | string,
  renderedOpenings: string[],
): Promise<EnsureConversationIntroSlotResponse> {
  const { data, error } = await supabase.rpc('rpc_ensure_conversation_intro_slot_v1', {
    p_conversation_id: Number(conversationId),
    p_openings: renderedOpenings,
  });

  if (error) throw error;
  if (!isRecord(data)) throw new Error('Invalid intro seeding response');

  const status = String(data.status);
  if (status !== 'seeded' && status !== 'existing' && status !== 'empty') {
    throw new Error('Invalid intro seeding status');
  }

  return {
    status,
    message_id: data.message_id == null ? null : Number(data.message_id),
  };
}
